import { App, AutoComplete, Space, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import type { AnalysisAgentType } from "./AnalysisAgentType";
import ActionButton from "../../masterComponents/actionButton";
import { useFetchData } from "../../../hooks/useFetch/post";
import { useFetchData as usePagination } from "../../../hooks/useFetch/pagination";
import "./analysisAgent.css";
import Filters from "../../masterComponents/Filters";
import { useFiltersContext } from "../../../contexts/filtersContext";
import { HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { StyledAnalysisActions, StyledPrompt } from "./analysisAgent.style";
import { useCommonPropertiesContext } from "../../../contexts/commonPropertiesContext";
import Icon from "../../masterComponents/Icon";
import {
  ALL_BATCHES,
  DEFAULT_OPEN_KEYS_AAGENT,
} from "../../../entities/entities";
import { getPercentage, onSorter } from "../../../utils";
import { ContainerWrapper, SpinWrapper } from "../../../app/app.style";
import { PHRASES_DICTIONARY } from "../../../entities/phrasesDictionary";
import { buildApiFrequency } from "../../../utils/filtersForStatus";
import {
  AnalysisAgentProvider,
  useAnalysisAgentContext,
} from "./_analysisAgentContext";
import DeferredActionsList from "./_ui/widgets/DeferredActionsList";
import AnalysisAgentTable from "./_ui/layouts/AnalysisAgentTable";
import ExpandedChildTables from "./_ui/layouts/ExpandedChildTables";
import ExamplesAttrAnalyzerTable from "./_ui/layouts/ExamplesAttrAnalyserTable";
import { LoadingOutlined } from "@ant-design/icons";
import type {
  IAttrStatChildSubmit,
  AttrStatDataType,
  IAttrsStatData,
} from "./UseFetchType";
import { useFilterStructure } from "../../../hooks/useFilterStructure/useFilterStructure";
import { buildFilterStructure } from "../../../utils/filtersStructureHelpers";
import ReviewSourceValuesTable from "./_ui/layouts/ReviewSourceValuesTable";
import { useSearchParams } from "react-router-dom";
import { PayloadCheckboxesActionsProvider } from "./_payloadCheckboxesActionsContext";
import { useAttrs } from "../../../hooks/useEndpoints/useAttrs";
import { getBatchLevelConfig } from "../../../utils/batchLevels";

const source = "finals";

const AnalysisAgentContent: React.FC<AnalysisAgentType> = () => {
  const { message: messageApi } = App.useApp();

  const { currentBatch } = useCommonPropertiesContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [pageSize, setPageSize] = useState(25);

  const {
    deferredActionsData,
    setDeferredActionsData,
    parentRowExamplesParams,
    filtersForStatus,
    setFiltersForStatus,
    tableSort,
    setTableSort,
    attributeToAnalyze,
    setAttributeToAnalyze,
    setSelectedBatches,
    selectedBatches,
    parentRowSourceValuesParams,
  } = useAnalysisAgentContext();

  const parentBatchLevel = useMemo(
    () => getBatchLevelConfig(ALL_BATCHES, selectedBatches, 0),
    [selectedBatches],
  );

  const {
    appliedFilters,
    setAppliedFilters,
    ignoredFilter,
    setIgnoredFilter,
    updateFilter,
  } = useFiltersContext();
  const {
    availableFilters: availableAgentFilters,
    activeFilters,
    classNameFilterValues,
  } = useFilterStructure("analysisAgent");

  const { attributes, isLoading: isLoadingAttributes } = useAttrs({
    classNames: classNameFilterValues,
    operation: "union",
    messageApi,
  });

  const [attrInputValue, setAttrInputValue] = useState("");

  const attrsOptions = useMemo(
    () =>
      attributes?.map((attr) => ({
        value: attr.attr_code,
        label: attr.attr_descr,
      })) ?? [],
    [attributes],
  );

  useEffect(() => {
    const queryAttrs = searchParams.get("attr");

    if (!queryAttrs || !attrsOptions?.length) return;

    const opt = attrsOptions.find((o) => o.value === queryAttrs);
    if (opt) {
      setAttributeToAnalyze(opt.value);
      setAttrInputValue(`${opt.value} (${opt.label})`);
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("attr");
    setSearchParams(newParams, { replace: true });
  }, [attrsOptions, searchParams]);

  useEffect(() => {
    if (
      attributeToAnalyze &&
      !attrsOptions.some((opt) => opt.value === attributeToAnalyze)
    ) {
      setAttributeToAnalyze("");
      setAttrInputValue("");
    }
  }, [attributeToAnalyze, attrsOptions]);

  const filterStructure = buildFilterStructure(activeFilters);

  const buildRequestParams = () => {
    const sort = onSorter(tableSort);
    const frequency = buildApiFrequency(filtersForStatus);

    return {
      sort,
      frequency,
      filters: filterStructure,
    };
  };

  const params = buildRequestParams();

  const [
    fetchAttributeStatistics,
    {
      isLoading: isLoadingAttributeStatisticsData,
      data: attributeStatisticsData,
      page,
      totalRows,
      error: attributeStatisticsError,
      loadMore,
      clearError: clearAttributeStatisticsError,
    },
  ] = usePagination<AttrStatDataType[]>({
    perPage: pageSize,
    optionalArray: [attributeToAnalyze, currentBatch, classNameFilterValues],
    query: "/@batch/statistics/attributes/@attributeName",
    selectData: (data: { items: IAttrsStatData[] }) =>
      data.items.map((statItem) => ({
        verifiedPercentage: statItem.sources.finals
          ? getPercentage(statItem.sources.finals, statItem.frequency)
          : 0,
        hash: statItem.hash,
        hash_value: `${statItem["hash"]}_${statItem["value"]}`,
        value: statItem.value,
        frequency: statItem.frequency,
        sources: Object.keys(statItem.sources).map((key) => ({
          source: key,
          count: statItem.sources[key] ?? 0,
          percentage: getPercentage(
            statItem.sources[key] ?? 0,
            statItem.frequency,
          ),
        })),
      })),
    selectPageInfo: (data) => data.page_info,
  });

  useEffect(() => {
    if (attributeStatisticsError) {
      messageApi.error(attributeStatisticsError.message);
      clearAttributeStatisticsError?.();
    }
  }, [attributeStatisticsError, messageApi]);

  const [
    fetchAttrStatChildSubmit,
    {
      isLoading: isLoadingAttrStatChildSubmit,
      data: attrStatChildSubmitData,
      error: attrStatChildSubmitError,
      clearError: clearAttrStatChildSubmitError,
    },
  ] = useFetchData<IAttrStatChildSubmit>({
    query: "/@batch/statistics/child/attributes/@sourceAttributeName/submit",
    selectData: (data: IAttrStatChildSubmit) => data,
  });

  useEffect(() => {
    if (attrStatChildSubmitError) {
      messageApi.error(attrStatChildSubmitError.message);
      clearAttrStatChildSubmitError?.();
    }
  }, [attrStatChildSubmitError, messageApi]);

  useEffect(() => {
    if (attrStatChildSubmitData?.result && !isLoadingAttrStatChildSubmit)
      fetchAttributeStatistics(
        { batch: currentBatch, attributeName: attributeToAnalyze },
        params,
      );
  }, [isLoadingAttrStatChildSubmit]);

  const [
    fetchAttrStatChildValidation,
    {
      isLoading: isLoadingAttrStatChildValidation,
      data: attrStatChildValidationData,
      error: attrStatChildValidationError,
      clearError: clearAttrStatChildValidationError,
    },
  ] = useFetchData<any>({
    query:
      "/@batch/statistics/child/attributes/@sourceAttributeName/submit-validation",
    selectData: (data: any) => data,
  });

  useEffect(() => {
    if (attrStatChildValidationError) {
      messageApi.error(attrStatChildValidationError.message);
      clearAttrStatChildValidationError?.();
    }
  }, [attrStatChildValidationError, messageApi]);

  useEffect(() => {
    if (
      attrStatChildValidationData?.result &&
      !isLoadingAttrStatChildValidation
    ) {
      fetchAttributeStatistics(
        { batch: currentBatch, attributeName: attributeToAnalyze },
        params,
      );
    }
  }, [isLoadingAttrStatChildValidation]);

  useEffect(() => {
    setSelectedBatches([currentBatch]);
  }, [currentBatch]);

  useEffect(() => {
    if (!attributeToAnalyze || !currentBatch) return;
    fetchAttributeStatistics(
      { batch: currentBatch, attributeName: attributeToAnalyze },
      params,
    );
  }, [filtersForStatus, tableSort, pageSize]);

  return (
    <ContainerWrapper $paddingMode="top" $withoutGap="true">
      <Filters
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        currentAvailableFilters={availableAgentFilters}
        ignoredFilter={ignoredFilter}
        setIgnoredFilter={setIgnoredFilter}
        updateFilter={updateFilter}
        defaultOpenKeys={DEFAULT_OPEN_KEYS_AAGENT}
      />
      <StyledAnalysisActions>
        <Space>
          Characteristic:
          <AutoComplete
            placeholder="Select Characteristic"
            style={{ width: 500 }}
            dropdownStyle={{ zIndex: 900 }}
            value={attrInputValue}
            options={attrsOptions}
            onChange={setAttrInputValue}
            onSelect={(val, option) => {
              setAttributeToAnalyze(val);
              setAttrInputValue(`${val} (${option?.label})`);
            }}
            onBlur={() => {
              if (attributeToAnalyze && attrInputValue !== attributeToAnalyze) {
                const option = attrsOptions.find(
                  (o) => o.value === attributeToAnalyze,
                );
                if (option) {
                  setAttrInputValue(`${option.value} (${option.label})`);
                }
              }
            }}
            optionRender={(option) => (
              <span>
                {option.value} ({option.label})
              </span>
            )}
            filterOption={(inputValue, option) => {
              if (!option?.value || !option?.label) return false;
              const search = inputValue.toLowerCase();
              return (
                String(option.value).toLowerCase().includes(search) ||
                String(option.label).toLowerCase().includes(search)
              );
            }}
            popupRender={(menu) =>
              isLoadingAttributes ? (
                <SpinWrapper style={{ justifyContent: "center" }}>
                  <Spin indicator={<LoadingOutlined />} />
                </SpinWrapper>
              ) : (
                menu
              )
            }
          />
          {!attributeToAnalyze && (
            <StyledPrompt>
              {PHRASES_DICTIONARY.characteristic_required_field}
            </StyledPrompt>
          )}
        </Space>
        <Space>
          <ActionButton
            text="Check acceptable values"
            className="primary-btn-color"
            disabled={true}
            iconPosition="end"
          />
          <ActionButton
            text="Search"
            className="primary-btn-color"
            disabled={
              !source || !attributeToAnalyze || isLoadingAttributeStatisticsData
            }
            onClick={() => {
              if (!tableSort && !filtersForStatus) {
                fetchAttributeStatistics(
                  { batch: currentBatch, attributeName: attributeToAnalyze },
                  { filters: filterStructure },
                );
              } else {
                setFiltersForStatus(null);
                setTableSort(null);
              }
            }}
            loading={isLoadingAttributeStatisticsData}
            icon={<Icon icon={HiOutlineMagnifyingGlass} />}
            iconPosition="end"
          />
        </Space>
      </StyledAnalysisActions>
      {Boolean(attributeStatisticsData?.length) && (
        <>
          {Boolean(deferredActionsData?.length) && (
            <DeferredActionsList
              deferredActionsData={deferredActionsData}
              removeListRow={(hash) => {
                setDeferredActionsData((prev) =>
                  prev.filter((item) => item.hash !== hash),
                );
              }}
              handleAttrStatChildSubmit={(actions) =>
                fetchAttrStatChildSubmit(
                  {
                    batch: currentBatch,
                    sourceAttributeName: attributeToAnalyze,
                  },
                  { child_batch: currentBatch, actions },
                )
              }
              isLoadingExecuted={isLoadingAttrStatChildSubmit}
              disabled={isLoadingAttributeStatisticsData}
            />
          )}
          <AnalysisAgentTable
            fetchAttributeStatistics={() =>
              fetchAttributeStatistics(
                { batch: currentBatch, attributeName: attributeToAnalyze },
                params,
              )
            }
            handleAttrStatChildSubmit={(actions) =>
              fetchAttrStatChildSubmit(
                {
                  batch: currentBatch,
                  sourceAttributeName: attributeToAnalyze,
                },
                { child_batch: currentBatch, actions },
              )
            }
            isLoadingAttrStatChildSubmit={isLoadingAttrStatChildSubmit}
            disabled={isLoadingAttributeStatisticsData}
            attributeStatisticsData={attributeStatisticsData}
            page={page}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRows={totalRows}
            loadMore={loadMore}
            handleAttrStatChildValidation={(actions) =>
              fetchAttrStatChildValidation(
                {
                  batch: currentBatch,
                  sourceAttributeName: attributeToAnalyze,
                },
                { child_batch: currentBatch, actions },
              )
            }
            isLoadingAttrStatChildValidation={isLoadingAttrStatChildValidation}
            renderExpanded={
              parentBatchLevel.canExpand
                ? (record) => (
                    <ExpandedChildTables
                      parentHash={record.hash}
                      parentFrequency={record.frequency}
                      fetchAttributeStatistics={() =>
                        fetchAttributeStatistics(
                          {
                            batch: currentBatch,
                            attributeName: attributeToAnalyze,
                          },
                          params,
                        )
                      }
                      isLoadingAttrStatChildSubmit={
                        isLoadingAttrStatChildSubmit
                      }
                      isLoadingAttrStatChildValidation={
                        isLoadingAttrStatChildValidation
                      }
                      disabled={isLoadingAttributeStatisticsData}
                    />
                  )
                : undefined
            }
          />
          {parentRowExamplesParams.hash &&
            parentRowExamplesParams.targetTable && (
              <ExamplesAttrAnalyzerTable />
            )}
          {parentRowSourceValuesParams.hash &&
            parentRowSourceValuesParams.targetTable && (
              <ReviewSourceValuesTable
                currentBatch={currentBatch}
                fetchAttributeStatistics={() =>
                  fetchAttributeStatistics(
                    { batch: currentBatch, attributeName: attributeToAnalyze },
                    params,
                  )
                }
                disabled={isLoadingAttributeStatisticsData}
              />
            )}
        </>
      )}
    </ContainerWrapper>
  );
};

const AnalysisAgent: React.FC<AnalysisAgentType> = (props) => (
  <AnalysisAgentProvider>
    <PayloadCheckboxesActionsProvider>
      <AnalysisAgentContent {...props} />
    </PayloadCheckboxesActionsProvider>
  </AnalysisAgentProvider>
);

export default AnalysisAgent;
